import { Security as AdminIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { PageHeader } from '@/components/page-header';
import { useFirebase } from '@/firebase';
import { useResponsiveValue } from '@/hooks/use-responsive-value';
import { useSubscription } from '@/hooks/useSubscription';
import { FeatureFlags, FeatureMetadata, SubscriptionPlan } from '@/types/subscription';

import { AdminStatsSection } from '../features/admin/components/AdminStatsSection';
import { AiMatrixEditor } from '../features/admin/components/AiMatrixEditor';
import { ExampleNovelsSection } from '../features/admin/components/ExampleNovelsSection';
import { FeatureEditDialog } from '../features/admin/components/FeatureEditDialog';
import { FeatureRegistry } from '../features/admin/components/FeatureRegistry';
import { PlanEditDialog } from '../features/admin/components/PlanEditDialog';
import { UserManagementSection } from '../features/admin/components/UserManagementSection';
import { AdminStats, TargetUser } from '../features/admin/types';

export const Route = createFileRoute('/adeptus-administratum')({
  component: AdminPage,
});

export default function AdminPage() {
  const { backend, isUserLoading } = useFirebase();
  const { isAdmin, loading: subLoading } = useSubscription();
  const resolveResponsive = useResponsiveValue();
  const navigate = useNavigate();

  // State
  const [tab, setTab] = useState(0);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [searchId, setSearchId] = useState('');
  const [targetUser, setTargetUser] = useState<TargetUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newPlanId, setNewPlanId] = useState<string>('free');
  const [newStatus, setNewStatus] = useState<string>('active');

  const [features, setFeatures] = useState<FeatureMetadata[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [planFlags, setPlanFlags] = useState<Record<string, FeatureFlags>>({});
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [featureModalOpen, setFeatureModalOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState<Partial<FeatureMetadata> | null>(null);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Partial<SubscriptionPlan> | null>(null);

  useEffect(() => {
    if (isAdmin && backend) {
      loadStats();
      loadSubscriptionConfigs();
    }
  }, [isAdmin, backend]);

  const loadSubscriptionConfigs = async () => {
    if (!backend) return;
    setFeaturesLoading(true);
    try {
      const featuresData = await backend.billing.fetchGlobalFeatures();
      setFeatures(featuresData.sort((a, b) => a.key.localeCompare(b.key)));

      const plansData = await backend.billing.fetchSubscriptionPlans();
      const plansList = plansData.sort((a, b) => a.displayOrder - b.displayOrder);
      setPlans(plansList);

      const flagsMap: Record<string, FeatureFlags> = {};
      for (const plan of plansList) {
        flagsMap[plan.id] = await backend.billing.fetchPlanFeatureFlags(plan.id);
      }
      setPlanFlags(flagsMap);
    } catch (err) {
      console.error('Error loading config:', err);
    } finally {
      setFeaturesLoading(false);
    }
  };

  const handleToggleFeature = async (planId: string, featureKey: string) => {
    if (!backend) return;
    const newValue = !(planFlags[planId]?.[featureKey]);
    try {
      await backend.billing.updatePlanFeatureFlag(planId, featureKey, newValue);
      setPlanFlags(prev => ({ ...prev, [planId]: { ...prev[planId], [featureKey]: newValue } }));
    } catch (err) {
      setError('Failed to update feature flag.');
    }
  };

  const handleSaveFeature = async (data: Partial<FeatureMetadata>) => {
    if (!backend || !data.key || !data.name) return;
    try {
      await backend.billing.saveFeature(data as FeatureMetadata);
      setSuccess(`Feature ${data.key} saved.`);
      setFeatureModalOpen(false);
      loadSubscriptionConfigs();
    } catch (err) {
      setError('Failed to save feature.');
    }
  };

  const handleSavePlan = async (data: Partial<SubscriptionPlan>) => {
    if (!backend || !data.id || !data.name) return;
    try {
      await backend.billing.saveSubscriptionPlan(data as SubscriptionPlan);
      setSuccess(`Plan ${data.id} saved.`);
      setPlanModalOpen(false);
      loadSubscriptionConfigs();
    } catch (err) {
      setError('Failed to save plan.');
    }
  };

  const handleDeleteFeature = async (key: string) => {
    if (!window.confirm(`Delete feature ${key}?`)) return;
    try {
      setSuccess(`Feature ${key} deleted.`);
      loadSubscriptionConfigs();
    } catch (err) {
      setError('Failed to delete feature.');
    }
  };

  const loadStats = async () => {
    if (!backend) return;
    setLoading(true);
    try {
      const statsData = await backend.billing.getAdminStats();
      setStats(statsData);
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : '';
      setError('Failed to load stats: ' + errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!backend || !searchId.trim()) return;
    setLoading(true);
    setTargetUser(null);
    try {
      const userData = await backend.billing.getUserByAdmin(searchId.trim());
      setTargetUser(userData);
      setNewPlanId(userData.planId || 'free');
      setNewStatus(userData.subscription?.status || 'active');
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : '';
      setError('User not found: ' + errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!backend || !targetUser) return;
    setActionLoading(true);
    try {
      await backend.billing.updateUserSubscription(targetUser.id, newPlanId, newStatus);
      setSuccess('Subscription updated.');
      handleSearch();
      loadStats();
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : '';
      setError('Update failed: ' + errMsg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSyncAllFlags = async () => {
    if (!backend || !window.confirm('Sync ALL users?')) return;
    setSyncLoading(true);
    try {
      await backend.billing.syncAllUserFeatureFlags();
      setSuccess('Flags synced successfully.');
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : '';
      setError('Sync failed: ' + errMsg);
    } finally {
      setSyncLoading(false);
    }
  };

  if (isUserLoading || subLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;

  if (!isAdmin) {
    return (
      <Container maxWidth="sm" sx={{ py: 10, textAlign: 'center' }}>
        <Paper sx={{ p: 5, borderRadius: 3, bgcolor: 'error.dark', color: 'error.contrastText' }}>
          <AdminIcon sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 700 }}>403 - Access Denied</Typography>
          <Button variant="contained" sx={{ mt: 4, bgcolor: 'white', color: 'error.main' }} onClick={() => navigate({ to: '/' })}>Return</Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <PageHeader title="Adeptus Administratum" description="Imperial processing and oversight." />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Overview" />
          <Tab label="Features" />
          <Tab label="AI Matrix" />
        </Tabs>
      </Box>

      {tab === 0 && (
        <Grid container spacing={4}>
          <AdminStatsSection stats={stats} loading={loading} onRefresh={loadStats} resolveResponsive={resolveResponsive} />

          <UserManagementSection
            searchId={searchId} setSearchId={setSearchId}
            targetUser={targetUser} onSearch={handleSearch} onUpdate={handleUpdate}
            loading={loading} actionLoading={actionLoading} error={error} success={success}
            newPlanId={newPlanId} setNewPlanId={setNewPlanId} newStatus={newStatus} setNewStatus={setNewStatus}
            plans={plans}
            resolveResponsive={resolveResponsive}
          />

          <Grid size={resolveResponsive({ xs: 12, md: 6 })}>
            <Card sx={{ height: '100%', borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(200, 150, 25, 0.03)' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Protocol Instructions</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>High-level administrative oversight for citizenship and feature access.</Typography>
              </CardContent>
            </Card>
          </Grid>

          <ExampleNovelsSection resolveResponsive={resolveResponsive} />

        </Grid>
      )}

      {tab === 1 && (
        <FeatureRegistry
          features={features} plans={plans} planFlags={planFlags}
          loading={featuresLoading} syncLoading={syncLoading}
          onSync={handleSyncAllFlags} onNewFeature={() => { setEditingFeature({}); setFeatureModalOpen(true); }}
          onToggleFeature={handleToggleFeature}
          onEditFeature={(f) => { setEditingFeature(f); setFeatureModalOpen(true); }}
          onDeleteFeature={handleDeleteFeature}
          onNewPlan={() => { setEditingPlan({}); setPlanModalOpen(true); }}
          onEditPlan={(p) => { setEditingPlan(p); setPlanModalOpen(true); }}
        />
      )}

      {tab === 2 && (
        <AiMatrixEditor />
      )}

      <FeatureEditDialog
        open={featureModalOpen} onClose={() => setFeatureModalOpen(false)}
        editingFeature={editingFeature} onSave={handleSaveFeature} onChange={setEditingFeature}
      />

      <PlanEditDialog
        open={planModalOpen} onClose={() => setPlanModalOpen(false)}
        editingPlan={editingPlan} onSave={handleSavePlan} onChange={setEditingPlan}
      />
    </Container>
  );
}
